// Customers' things as RowList rows (BL-189 consolidation; Equipment was EquipmentList, pages/ux-full
// components/customers.js EquipmentCard, as a list): what each row says, in words, with no drawing of
// its own. A piece of equipment says what it is and where, how old, its warranty and the last visit, and
// what's due; its name opens the customer's record on it. Its state is its word: a service overdue is
// red, one coming up and a warranty about to end are waiting (dashed), a retired unit is muted.
import type { Row } from '../components/RowList/RowList.tsx';
import type { EquipmentListProps, EquipmentRow } from '../customers-schemas.ts';
import { day, plural } from '../format.ts';

/** A service due within this many days is coming up; a warranty ending within this many is ending. */
export const DUE_SOON_DAYS = 30;
export const WARRANTY_SOON_DAYS = 60;

const daysFrom = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);

/** "11 years old", "Put in this year", or nothing when its install day isn't known. */
export function ageWords(e: Pick<EquipmentRow, 'ageYears'>): string | null {
  if (e.ageYears === null) return null;
  return e.ageYears === 0 ? 'Put in this year' : `${plural(e.ageYears, 'year')} old`;
}

export function warrantyWords(e: Pick<EquipmentRow, 'warranty'>): string {
  const w = e.warranty;
  if (w.state === 'covered') return w.lifetime ? 'Lifetime warranty' : `Covered to ${day(w.until)}`;
  if (w.state === 'ended') return w.until ? `Warranty ended ${day(w.until)}` : 'Warranty ended';
  return 'No warranty on file';
}

/** What needs someone about it, as a word and its tone; null when nothing does. */
export function dueOf(e: EquipmentRow, today: string): { word: string; text: string; bad: boolean } | null {
  if (e.retiredAt) return null;
  const s = e.serviceDue;
  if (s && s.on < today) return { word: 'Overdue', text: `${s.what} was due ${day(s.on)}`, bad: true };
  if (s && daysFrom(today, s.on) <= DUE_SOON_DAYS)
    return { word: 'Due soon', text: `${s.what} due ${day(s.on)}`, bad: false };
  const w = e.warranty;
  if (w.state === 'covered' && w.until && daysFrom(today, w.until) <= WARRANTY_SOON_DAYS)
    return { word: 'Warranty ending', text: `Warranty ends ${day(w.until)}`, bad: false };
  return null;
}

const where = (e: EquipmentRow) => `${e.customer.name}, ${e.location.name ?? e.location.line1}`;

/**
 * Every customer's equipment, one row each (equipment.list's answer, on the company's today). onOpen opens
 * the customer's record on a piece; brief is a side list, whose and what's due, on two lines.
 */
export const equipmentRows = (
  data: EquipmentListProps,
  { onOpen, brief = false }: { onOpen?: (e: EquipmentRow) => void; brief?: boolean } = {},
): Row[] =>
  data.equipment.map((e): Row => {
    const due = dueOf(e, data.today);
    const what = [e.make, e.model].filter(Boolean).join(' ');
    const facts = e.retiredAt
      ? [`Retired ${day(e.retiredAt.slice(0, 10))}`]
      : [
          ageWords(e),
          warrantyWords(e),
          e.lastVisit
            ? `Last visit ${day(e.lastVisit.on)}, job #${e.lastVisit.jobNumber}`
            : 'No visit there yet',
        ].filter((x): x is string => !!x);
    return {
      id: e.id,
      name: e.kind,
      ...(onOpen
        ? { onOpen: () => onOpen(e), openLabel: `${e.kind} at ${where(e)}: open their record` }
        : {}),
      lines: [
        { text: brief ? where(e) : [what, where(e), e.location.city].filter(Boolean).join(', ') },
        ...(brief
          ? due
            ? [{ tone: 'ink' as const, text: due.text }]
            : []
          : [{ tone: 'ink' as const, text: facts.join('. ') }, ...(due ? [{ text: due.text }] : [])]),
      ],
      ...(e.retiredAt
        ? { off: true, end: { word: 'Retired', tone: 'muted' as const } }
        : due
          ? { end: { word: due.word, tone: due.bad ? ('bad' as const) : ('wait' as const) } }
          : {}),
    };
  });
